import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ArrowLeft, Save } from "lucide-react";
import {
  createCategoryService,
  updateCategoryService,
  fetchCategoryServiceById,
  resetCategoryServiceStatuses,
} from "../../store/slices/categoryServicesSlice";
import { fetchCategories } from "../../store/slices/commonApiSlice";
import { TextField, CheckboxField } from "../../components/ui/FormControls";
import { showApiError, showApiSuccess } from "../../utils/toast";
import Loader from "../../components/Loader";

const categoryServiceSchema = yup.object({
  category_uuid: yup
    .string()
    .required("Category is required"),
  name: yup
    .string()
    .required("Service name is required")
    .max(255, "Service name must be less than 255 characters"),
  tagline: yup
    .string()
    .nullable()
    .max(500, "Tagline must be less than 500 characters"),
  status: yup.boolean(),
});

export default function AddCategoryServicePage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const editUuid = searchParams.get("edit");

  const {
    categoryService,
    categoryServiceCreateStatus,
    categoryServiceUpdateStatus,
    categoryServiceFetchStatus,
    loading,
  } = useSelector((state) => state.categoryServices);

  const { categories = [] } = useSelector((state) => state.common);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(categoryServiceSchema),
    defaultValues: {
      category_uuid: "",
      name: "",
      tagline: "",
      status: true,
    },
  });

  // Fetch categories for dropdown
  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  // Fetch service data if editing
  useEffect(() => {
    if (editUuid) {
      dispatch(fetchCategoryServiceById(editUuid));
    }
  }, [editUuid, dispatch]);

  // Populate form when service data is loaded
  useEffect(() => {
    if (editUuid && categoryService && categoryServiceFetchStatus === "success") {
      reset({
        category_uuid: categoryService.category?.uuid || "",
        name: categoryService.name || "",
        tagline: categoryService.tagline || "",
        status: Boolean(categoryService.status),
      });
    }
  }, [categoryService, categoryServiceFetchStatus, editUuid, reset]);

  const onSubmit = async (formData) => {
    try {
      const payload = {
        category_uuid: formData.category_uuid,
        name: formData.name,
        status: formData.status,
      };

      // Add tagline if provided
      if (formData.tagline) {
        payload.tagline = formData.tagline;
      }

      if (editUuid) {
        await dispatch(updateCategoryService({ uuid: editUuid, data: payload })).unwrap();
        showApiSuccess("Category service updated successfully");
      } else {
        await dispatch(createCategoryService(payload)).unwrap();
        showApiSuccess("Category service created successfully");
      }

      navigate("/category-services");
    } catch (error) {
      showApiError(null, error || `Failed to ${editUuid ? "update" : "create"} service`);
    }
  };

  useEffect(() => {
    return () => {
      dispatch(resetCategoryServiceStatuses());
    };
  }, [dispatch]);

  const isSubmitting =
    categoryServiceCreateStatus === "loading" || categoryServiceUpdateStatus === "loading";

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {loading && <Loader />}

      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate("/category-services")}
              className="text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {editUuid ? "Edit Category Service" : "Add New Category Service"}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {editUuid
                  ? "Update service information"
                  : "Create a new service linked to a category"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Service Information
          </h3>

          <div className="space-y-4">
            {/* Category Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                {...register("category_uuid")}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a category</option>
                {categories.map((category) => (
                  <option key={category.uuid} value={category.uuid}>
                    {category.name}
                  </option>
                ))}
              </select>
              {errors?.category_uuid && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.category_uuid.message}
                </p>
              )}
            </div>

            {/* Service Name */}
            <div>
              <TextField
                label="Service Name"
                required
                placeholder="Enter service name (e.g., Plumbing, Electrical, etc.)"
                {...register("name")}
                error={errors?.name?.message}
              />
            </div>

            {/* Tagline */}
            <div>
              <TextField
                label="Tagline"
                placeholder="Enter service tagline (optional)"
                {...register("tagline")}
                error={errors?.tagline?.message}
              />
              <p className="text-sm text-gray-500 mt-1">
                A short description or tagline for this service (max 500 characters)
              </p>
            </div>

            {/* Status */}
            <div>
              <Controller
                control={control}
                name="status"
                render={({ field }) => (
                  <CheckboxField
                    label="Active Status"
                    checked={Boolean(field.value)}
                    onChange={(checked) => field.onChange(checked)}
                  />
                )}
              />
              <p className="text-sm text-gray-500 mt-1">
                Enable this service to make it available for selection
              </p>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate("/category-services")}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="h-4 w-4" />
              <span>
                {isSubmitting
                  ? "Saving..."
                  : editUuid
                  ? "Update Service"
                  : "Create Service"}
              </span>
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
